import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
// Vite resolves these to served asset URLs.
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import type { ColorPreset, DeckColorConfig, DetectedCloze } from "../types";
import { COLOR_PRESETS, DEFAULT_MAGENTA_BAND, DETECT_SCALE } from "../types";
import { detectPage, type RunCandidate } from "../detect/detectPage";
import { runBox } from "../detect/runGeometry";
import { filterByHeight } from "../detect/heightFilter";
import type { PagePixels } from "../detect/pixelSampler";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

// cMaps + standard fonts are copied into public/pdfjs (see scripts/copy-pdfjs-assets.mjs).
// They are REQUIRED: without them, CJK (CID) fonts neither render nor extract text.
// Resolved from the site root (leading "/") so they load on nested routes like /app/read/5 too.
const CMAP_URL = new URL("/pdfjs/cmaps/", document.baseURI).toString();
const STANDARD_FONT_URL = new URL("/pdfjs/standard_fonts/", document.baseURI).toString();

export async function loadPdf(data: ArrayBuffer | Blob): Promise<PDFDocumentProxy> {
  const buf = data instanceof Blob ? await data.arrayBuffer() : data;
  return pdfjsLib.getDocument({
    data: new Uint8Array(buf),
    cMapUrl: CMAP_URL,
    cMapPacked: true,
    standardFontDataUrl: STANDARD_FONT_URL,
  }).promise;
}

export interface PageSize {
  width: number;
  height: number;
}

/** Page size in page coordinates (PDF points). */
export function pageSize(page: PDFPageProxy): PageSize {
  const vp = page.getViewport({ scale: 1 });
  return { width: vp.width, height: vp.height };
}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

// Serialize page renders: pdf.js rejects overlapping render() calls on one canvas,
// and rendering the same page proxy concurrently is unsafe. A simple promise chain
// keeps every render strictly sequential across the whole app.
let renderLock: Promise<unknown> = Promise.resolve();

/** Render a page into a 2D canvas at the given scale (serialized app-wide). The
 * optional shouldCancel is checked once the mutex frees, so a render queued for a
 * page that has since scrolled away / unmounted is skipped instead of rasterized.
 * Set willRead=true ONLY when the pixels will be read back (detection); for plain
 * display, leave it false so the canvas is GPU-accelerated (sharper + faster). */
export async function renderPage(
  page: PDFPageProxy,
  scale: number,
  canvas?: HTMLCanvasElement,
  shouldCancel?: () => boolean,
  willRead = false,
): Promise<HTMLCanvasElement> {
  const run = async () => {
    if (shouldCancel?.()) return canvas ?? makeCanvas(1, 1);
    const viewport = page.getViewport({ scale });
    const c = canvas ?? makeCanvas(Math.ceil(viewport.width), Math.ceil(viewport.height));
    c.width = Math.ceil(viewport.width);
    c.height = Math.ceil(viewport.height);
    const ctx = c.getContext("2d", { willReadFrequently: willRead })!;
    await page.render({ canvasContext: ctx, viewport, canvas: c }).promise;
    return c;
  };
  const result = renderLock.then(run, run);
  renderLock = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

function pixelsFrom(canvas: HTMLCanvasElement): PagePixels {
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { width: img.width, height: img.height, data: img.data };
}

/** Build sampling boxes (device px) for each text run on the page. */
export async function runCandidates(
  page: PDFPageProxy,
  scale: number,
): Promise<RunCandidate[]> {
  const viewport = page.getViewport({ scale });
  const tc = await page.getTextContent();
  const out: RunCandidate[] = [];
  for (const item of tc.items) {
    if (!("transform" in item)) continue; // skip TextMarkedContent
    const str = item.str;
    if (!str || !str.trim()) continue;
    const box = runBox(viewport.transform as number[], item.transform, item.width, scale);
    if (box.h <= 0) continue;
    out.push({ str, deviceBox: box });
  }
  return out;
}

/** Extract a page's plain text for AI question generation (pdf.js getTextContent). pageIndex is
 * 0-based (pdf.js pages are 1-based). Uses pdf.js's own hasEOL to reconstruct line breaks. */
export async function getPageText(doc: PDFDocumentProxy, pageIndex: number): Promise<string> {
  const page = await doc.getPage(pageIndex + 1);
  try {
    const tc = await page.getTextContent();
    let out = "";
    for (const item of tc.items) {
      if (!("str" in item)) continue; // skip TextMarkedContent
      out += item.str;
      if (item.hasEOL) out += "\n";
    }
    return out.replace(/\n{3,}/g, "\n\n").trim();
  } finally {
    page.cleanup();
  }
}

// Render detection at a lower scale on memory-constrained touch devices (iOS Safari
// jettisons the page otherwise). Answer-term detection is unaffected: a glyph's
// in-band pixel count stays well above the threshold even at this scale.
const MOBILE_DETECT_SCALE = 1.5;
function detectScale(): number {
  try {
    if (typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches)
      return MOBILE_DETECT_SCALE;
  } catch {
    /* fall through to desktop scale */
  }
  return DETECT_SCALE;
}

/** Render page 1 of a PDF to a small JPEG cover thumbnail. */
export async function renderCover(data: ArrayBuffer | Blob, maxWidth = 240): Promise<Blob> {
  const doc = await loadPdf(data);
  try {
    const page = await doc.getPage(1);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const vp1 = page.getViewport({ scale: 1 });
    const canvas = await renderPage(page, (maxWidth / vp1.width) * dpr);
    page.cleanup();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.82),
    );
    canvas.width = 0;
    canvas.height = 0;
    if (!blob) throw new Error("cover render failed");
    return blob;
  } finally {
    await doc.loadingTask.destroy();
  }
}

/** Render any page of an already-open PDF to a small JPEG thumbnail (the quiz page picker). */
export async function renderPageImage(
  doc: PDFDocumentProxy,
  pageIndex: number,
  maxWidth = 200,
): Promise<Blob> {
  const page = await doc.getPage(pageIndex + 1);
  try {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const vp1 = page.getViewport({ scale: 1 });
    const canvas = await renderPage(page, (maxWidth / vp1.width) * dpr);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.8),
    );
    canvas.width = 0;
    canvas.height = 0;
    if (!blob) throw new Error("page render failed");
    return blob;
  } finally {
    page.cleanup();
  }
}

/** Detect colored answers on a single already-open page (used by the live tuner). */
export async function detectOnPage(
  page: PDFPageProxy,
  cfg: DeckColorConfig,
  scale: number,
  canvas?: HTMLCanvasElement,
): Promise<DetectedCloze[]> {
  const c = await renderPage(page, scale, canvas, undefined, true); // willRead: detection reads pixels
  const px = pixelsFrom(c);
  const runs = await runCandidates(page, scale);
  return detectPage(page.pageNumber - 1, px, runs, cfg, scale);
}

function configForPreset(p: ColorPreset): DeckColorConfig {
  return { ...DEFAULT_MAGENTA_BAND, hueTarget: p.hueTarget, hueTol: p.hueTol };
}

/** Evenly-spaced sample page indices (0-based), capped at `max`. */
function samplePageIndices(total: number, max = 6): number[] {
  if (total <= max) return Array.from({ length: total }, (_, i) => i);
  const set = new Set<number>();
  for (let i = 0; i < max; i++) set.add(Math.floor(((i + 0.5) / max) * total));
  return [...set];
}

/**
 * Auto-pick the answer color for a first import: render a handful of sample pages (once each) and
 * classify them with EVERY preset, then return the config that finds the most colored answers.
 * Reuses the real detection so "what counts as an answer" is identical to the full pass. Falls back
 * to the magenta default when nothing colored is found (the user can still change the color and
 * re-detect afterwards). Cheap — one render per sample page, four classification passes per render.
 */
export async function autoDetectColorConfig(
  data: ArrayBuffer | Blob,
  signal?: AbortSignal,
): Promise<DeckColorConfig> {
  const doc = await loadPdf(data);
  try {
    const pages = samplePageIndices(doc.numPages);
    const scale = detectScale();
    const canvas = makeCanvas(1, 1);
    const scores = COLOR_PRESETS.map(() => 0);
    try {
      for (const pi of pages) {
        if (signal?.aborted) throw new CancelledError();
        const page = await doc.getPage(pi + 1);
        try {
          const c = await renderPage(page, scale, canvas, undefined, true);
          const px = pixelsFrom(c);
          const runs = await runCandidates(page, scale);
          for (let i = 0; i < COLOR_PRESETS.length; i++) {
            scores[i] += detectPage(pi, px, runs, configForPreset(COLOR_PRESETS[i]), scale).length;
          }
        } finally {
          page.cleanup();
        }
        await yieldToUI();
      }
    } finally {
      canvas.width = 0;
      canvas.height = 0;
    }
    let best = 0;
    for (let i = 1; i < scores.length; i++) if (scores[i] > scores[best]) best = i;
    // Require a small floor so a stray false-positive doesn't pick a wrong color.
    if (scores[best] < 2) return DEFAULT_MAGENTA_BAND;
    return configForPreset(COLOR_PRESETS[best]);
  } finally {
    await doc.loadingTask.destroy();
  }
}

/** Detect on one page of an open document (for tuner preview). */
export async function detectSinglePage(
  doc: PDFDocumentProxy,
  pageIndex: number,
  cfg: DeckColorConfig,
): Promise<DetectedCloze[]> {
  const page = await doc.getPage(pageIndex + 1);
  try {
    return await detectOnPage(page, cfg, detectScale());
  } finally {
    page.cleanup();
  }
}

export interface OutlineBookmark {
  title: string;
  pageIndex: number;
}

type OutlineNode = NonNullable<Awaited<ReturnType<PDFDocumentProxy["getOutline"]>>>[number];

/** Resolve an outline item's destination (named or explicit) to a 0-based page index. */
async function destPageIndex(doc: PDFDocumentProxy, dest: OutlineNode["dest"]): Promise<number | null> {
  try {
    const explicit = typeof dest === "string" ? await doc.getDestination(dest) : dest;
    const ref = Array.isArray(explicit) ? explicit[0] : null;
    if (!ref) return null;
    return await doc.getPageIndex(ref);
  } catch {
    return null;
  }
}

/** Read the PDF's built-in outline (目次/しおり) as flat bookmarks, indenting nested entries. */
export async function extractOutline(doc: PDFDocumentProxy): Promise<OutlineBookmark[]> {
  let nodes: OutlineNode[] | null = null;
  try {
    nodes = await doc.getOutline();
  } catch {
    return [];
  }
  if (!nodes?.length) return [];
  const out: OutlineBookmark[] = [];
  const walk = async (items: OutlineNode[], depth: number): Promise<void> => {
    for (const it of items) {
      const title = (it.title ?? "").trim();
      const pageIndex = await destPageIndex(doc, it.dest);
      if (title && pageIndex != null) {
        out.push({ title: depth > 0 ? "　".repeat(depth) + title : title, pageIndex });
      }
      if (it.items?.length) await walk(it.items, depth + 1);
    }
  };
  await walk(nodes, 0);
  return out;
}

export interface PdfDetectionResult {
  pageCount: number;
  pageW: number;
  pageH: number;
  clozes: DetectedCloze[];
  /** The PDF's built-in outline (目次) as bookmarks, if any. */
  outline: OutlineBookmark[];
}

const yieldToUI = () => new Promise<void>((r) => setTimeout(r, 0));

export class CancelledError extends Error {
  constructor() {
    super("cancelled");
    this.name = "CancelledError";
  }
}

/**
 * Render + detect colored answers across every page of a PDF. Cleans up each page
 * as it goes (bounded memory) and yields between pages so the UI stays responsive.
 * Pass an AbortSignal to cancel mid-run. A height-outlier pass drops heading
 * false-positives at the end.
 */
export async function detectClozesInPdf(
  data: ArrayBuffer | Blob,
  cfg: DeckColorConfig,
  onProgress?: (page: number, total: number, found: number) => void,
  signal?: AbortSignal,
): Promise<PdfDetectionResult> {
  // Breadcrumb so a failure on a device reports which step/page broke (the message is
  // surfaced in the import error UI; the full stack is mapped via sourcemaps).
  let stage = "loadPdf";
  try {
    const doc = await loadPdf(data);
    const pageCount = doc.numPages;
    const canvas = makeCanvas(1, 1);
    const scale = detectScale();
    const cleanupEvery = scale < DETECT_SCALE ? 8 : 16;
    let clozes: DetectedCloze[] = [];
    let outline: OutlineBookmark[] = [];
    let pageW = 0;
    let pageH = 0;
    try {
      for (let p = 1; p <= pageCount; p++) {
        if (signal?.aborted) throw new CancelledError();
        stage = `getPage ${p}`;
        const page = await doc.getPage(p);
        try {
          if (p === 1) {
            const sz = pageSize(page);
            pageW = sz.width;
            pageH = sz.height;
          }
          stage = `detect ${p}`;
          clozes.push(...(await detectOnPage(page, cfg, scale, canvas)));
        } finally {
          page.cleanup();
        }
        onProgress?.(p, pageCount, clozes.length);
        await yieldToUI();
        // Periodically flush pdf.js caches to keep memory bounded (matters on iOS,
        // which kills the page if a long render run uses too much memory).
        if (p % cleanupEvery === 0) await doc.cleanup();
      }
      stage = "filterByHeight";
      clozes = filterByHeight(clozes, cfg.maxHeightRatio);
      stage = "outline";
      outline = await extractOutline(doc);
    } finally {
      canvas.width = 0;
      canvas.height = 0;
      await doc.loadingTask.destroy();
    }
    return { pageCount, pageW, pageH, clozes, outline };
  } catch (e) {
    if (e instanceof CancelledError) throw e;
    if (e instanceof Error) e.message = `${e.message} [stage=${stage}, build=${__BUILD_ID__}]`;
    throw e;
  }
}
